import React from "react";
import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { MapPin, GraduationCap, Briefcase, ShieldCheck, ArrowRight, Users } from "lucide-react";

export default function CentreLanding() {
  useSeo({
    title: "Teacher4Me Learning Centre — In-person & online tutoring in Calgary NW",
    description:
      "Real teachers, personalised learning plans, weekly parent progress reports. Enroll your child at Teacher4Me Learning Centre — Calgary NW.",
    canonical: "https://teacher4me.ca/centre",
  });

  return (
    <div className="max-w-6xl mx-auto px-6 py-14">
      <div className="grid md:grid-cols-2 gap-10 items-center">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold text-[#064E3B] uppercase tracking-wider">
            <MapPin className="w-4 h-4" /> Calgary NW · Physical + Online
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl mt-2 leading-tight">
            Real teachers.<br />Real progress.<br />
            <span className="text-[#D92D20]">Every week.</span>
          </h1>
          <p className="text-base text-[#4B5563] mt-4 max-w-lg">
            Teacher4Me Learning Centre pairs your child with an approved teacher for
            in-person or online tutoring — with a personalised learning plan and a
            weekly parent progress report delivered straight to your inbox.
          </p>
          <div className="flex flex-wrap gap-3 mt-6">
            <Link to="/register?role=parent" data-testid="centre-cta-enroll" className="btn-primary">
              Enroll your child <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
            <Link to="/register?role=teacher" data-testid="centre-cta-teach"
              className="btn-outline">
              Teach with us
            </Link>
          </div>
        </div>

        <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 shadow-sm">
          <div className="text-xs uppercase font-semibold text-[#4B5563] tracking-wider">
            What parents get
          </div>
          <ul className="mt-4 space-y-3 text-sm">
            <Bullet icon={GraduationCap}>Personalised learning plan for each child</Bullet>
            <Bullet icon={Users}>1-on-1 sessions with an approved teacher</Bullet>
            <Bullet icon={ShieldCheck}>Weekly progress report via email</Bullet>
            <Bullet icon={Briefcase}>Transparent monthly tuition, no hidden fees</Bullet>
          </ul>
          <div className="mt-6 border-t border-[#E5E7EB] pt-4 text-xs text-[#4B5563]">
            E-signed enrollment agreement · PIPEDA-compliant
          </div>
        </div>
      </div>

      <div className="mt-16 grid md:grid-cols-3 gap-6">
        <Step n="1" title="Enroll your child"
          body="Fill the 3-minute form, e-sign the Parent Agreement — done." />
        <Step n="2" title="We match a teacher"
          body="Approved teacher, right subject, schedule that fits your child." />
        <Step n="3" title="Weekly progress"
          body="Session reports and parent digest every week — you always know where they stand." />
      </div>
    </div>
  );
}

function Bullet({ icon: Icon, children }) {
  return (
    <li className="flex items-start gap-3">
      <Icon className="w-4 h-4 text-[#064E3B] mt-0.5 shrink-0" />
      <span>{children}</span>
    </li>
  );
}

function Step({ n, title, body }) {
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6">
      <div className="w-8 h-8 rounded-full bg-[#D92D20] text-white flex items-center justify-center font-bold">
        {n}
      </div>
      <div className="font-display font-bold text-lg mt-3">{title}</div>
      <p className="text-sm text-[#4B5563] mt-1">{body}</p>
    </div>
  );
}
